import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  Compass,
  Workflow,
  BookOpen,
  Sparkles,
  ArrowRight,
  Search,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Layers,
} from "lucide-react";
import { ExpandableCard } from "../common/ExpandableCard";
import { AssistantChat } from "../assistant/AssistantChat";
import { KNOWLEDGE_TOPICS } from "../../services/assistantKnowledge";

type GuideTab = "assistant" | "personas" | "workflow" | "instructions";

export const PlatformGuideView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<GuideTab>("assistant");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTopics = KNOWLEDGE_TOPICS.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.title.toLowerCase().includes(q) ||
      t.summary.toLowerCase().includes(q) ||
      t.content.toLowerCase().includes(q) ||
      t.keywords.some((kw) => kw.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="glass flex flex-col justify-between gap-4 rounded-2xl border border-slate-200/80 bg-white/70 p-6 shadow-xs sm:flex-row sm:items-center dark:border-slate-800/80 dark:bg-slate-900/70">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-semibold text-[#2ba5b5] dark:bg-teal-950/50 dark:text-teal-300">
              <Sparkles className="h-3.5 w-3.5" />
              Platform Guide & Manual
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <Layers className="h-3.5 w-3.5 text-[#2ba5b5]" />8 Workspace Stages
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            MLC Proposal HQ Platform Guide
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            End-to-end stage blueprint, stakeholder personas, step-by-step operating procedures, and
            interactive AI assistant for Multilingual Connections.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/workspace"
            className="btn-animate inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-2 text-sm font-semibold text-[#2ba5b5] shadow-xs hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
          >
            <span>RFP Workspace</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/"
            className="btn-animate inline-flex items-center gap-2 rounded-lg bg-[#2ba5b5] px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-[#238997]"
          >
            <span>Open Pipeline</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex items-center gap-2 overflow-x-auto border-b border-slate-200 pb-2 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab("assistant")}
          className={`btn-animate flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
            activeTab === "assistant"
              ? "bg-[#2ba5b5] text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          <span>Interactive Assistant</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("personas")}
          className={`btn-animate flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
            activeTab === "personas"
              ? "bg-[#2ba5b5] text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          }`}
        >
          <Compass className="h-4 w-4" />
          <span>Vision & Personas</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("workflow")}
          className={`btn-animate flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
            activeTab === "workflow"
              ? "bg-[#2ba5b5] text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          }`}
        >
          <Workflow className="h-4 w-4" />
          <span>End-to-End Workflow</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("instructions")}
          className={`btn-animate flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
            activeTab === "instructions"
              ? "bg-[#2ba5b5] text-white shadow-xs"
              : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          }`}
        >
          <BookOpen className="h-4 w-4" />
          <span>Step-by-Step Guides & FAQ</span>
        </button>
      </div>

      {/* Tab 1: Interactive Assistant */}
      {activeTab === "assistant" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-1">
            <div className="glass rounded-xl border border-slate-200/80 bg-white/70 p-5 dark:border-slate-800/80 dark:bg-slate-900/70">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">
                About the HQ Assistant
              </h2>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                The Proposal HQ Assistant is trained on the full system blueprint, company facts,
                and all 8 proposal development stages.
              </p>

              <div className="mt-4 space-y-2.5">
                <div className="flex items-start gap-2.5 rounded-lg bg-teal-50/60 p-3 text-xs dark:bg-teal-950/40">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[#2ba5b5]" />
                  <div>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Grounded Guidance:
                    </span>
                    <p className="text-slate-600 dark:text-slate-400">
                      Provides factual walkthroughs for all 8 workspace stages (Intake, Assessment,
                      Response Plan, Drafting, Coach, Red Team, Final Submission, Debrief & Learn)
                      without hallucination.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 rounded-lg bg-teal-50/60 p-3 text-xs dark:bg-teal-950/40">
                  <Zap className="h-4 w-4 shrink-0 text-[#2ba5b5]" />
                  <div>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Smart Deep Links:
                    </span>
                    <p className="text-slate-600 dark:text-slate-400">
                      Responses include direct links to relevant workspace stages, pipeline views,
                      planning tabs, library records, or schedule presets.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 rounded-lg bg-teal-50/60 p-3 text-xs dark:bg-teal-950/40">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                  <div>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Honest Claims Enforced:
                    </span>
                    <p className="text-slate-600 dark:text-slate-400">
                      MLC is a certified WBE; the assistant never manufactures false ISO
                      certifications.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass rounded-xl border border-slate-200/80 bg-white/70 p-5 dark:border-slate-800/80 dark:bg-slate-900/70">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Quick Navigation Links
              </h3>
              <div className="mt-3 space-y-1.5">
                <Link
                  to="/workspace"
                  className="flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <span>RFP Workspace & Switcher</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </Link>
                <Link
                  to="/"
                  className="flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <span>Opportunity Pipeline</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </Link>
                <Link
                  to="/library"
                  className="flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <span>Answer Library & Winning Content</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </Link>
                <Link
                  to="/reviews"
                  className="flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <span>SME & Review Queue</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <span>Schedule Presets & AI Settings</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <AssistantChat isCompact={false} />
          </div>
        </div>
      )}

      {/* Tab 2: Vision & Personas */}
      {activeTab === "personas" && (
        <div className="space-y-6">
          {/* Executive Vision Banner */}
          <div className="glass rounded-xl border border-teal-200/80 bg-linear-to-r from-teal-500/10 via-transparent to-transparent p-6 dark:border-teal-800/80">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Executive Vision & Strategic Objectives
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              Responding to Requests for Proposals (RFPs) is typically a high-stress, repetitive,
              and time-intensive process that pulls Subject Matter Experts (SMEs) away from billable
              work, risks missed deadlines, and suffers from inconsistent quality or outdated
              claims. The goal of this platform is not to replace the human bid team, but to
              function as a force-multiplier across the entire proposal lifecycle.
            </p>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-[#2ba5b5]">70–80%</span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Draft Assembly Reduction
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Using historical winning proposal intelligence.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                  0%
                </span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Non-Compliance Risk
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Automated requirement shredding and matrix audits.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-indigo-600 dark:text-indigo-400">
                  Stage 3 Plan
                </span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Frozen Response Baseline
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Milestones, addenda, and section ownership.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-teal-600 dark:text-teal-400">
                  Stage 7 Gate
                </span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Dual Sign-Off Gate
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Master compiler & executive authorization.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-rose-500">Red-Teamer</span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  AI Client Evaluator
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Stress-tests arguments prior to submission.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200/80 bg-white/80 p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-800/80">
                <span className="text-xl font-black text-purple-600 dark:text-purple-400">
                  FOIA & Rebid
                </span>
                <p className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Post-Mortem & Rebid
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Competitor scorecards & recompete cloning.
                </p>
              </div>
            </div>
          </div>

          {/* Stakeholder Personas & Value Matrix */}
          <div className="space-y-4">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Stakeholder Personas & Value Matrix
            </h2>

            <ExpandableCard
              title="Proposal / Bid Manager"
              badge="Timeline, Planning & Compliance"
              defaultExpanded={true}
              summary="Orchestrates response milestones, addenda, section allocation, freeze gates, master compilation, and submission without chasing staff."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <strong className="text-slate-900 dark:text-white">Primary Role:</strong> Oversees
                  the entire proposal cycle from intake to submission sign-off.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Pain Points Solved:</strong>{" "}
                  Tracking unanswered questions across 100+ pages, uncoordinated milestone
                  deadlines, scope creep from late client addenda, chasing SMEs over email, and
                  unverified submission guidelines.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Key Features Used:</strong>
                  <ul className="mt-1.5 list-inside list-disc space-y-1 pl-2">
                    <li>
                      <strong>Stage 3 Milestones & Auto-Scheduler:</strong> Auto-generates
                      color-team milestones (Pink, Red, Gold, Final Sign-off) with business-day
                      offsets.
                    </li>
                    <li>
                      <strong>RFP Addenda & Scope Shift Log:</strong> Tracks amendments, impact
                      ratings, and flags affected requirements in real time.
                    </li>
                    <li>
                      <strong>Requirements Matrix Bulk Actions:</strong> Bulk assign owners, verify
                      compliance, and view category completion rollups.
                    </li>
                    <li>
                      <strong>Bid Section Ownership & Workload Allocation:</strong> Assigns section
                      leads, writers, and SME reviewers while monitoring team capacity.
                    </li>
                    <li>
                      <strong>Plan Readiness Audit & Freeze Gate:</strong> 5-point automated audit
                      and immutable baseline lock before drafting begins.
                    </li>
                    <li>
                      <strong>1-Page Executive Proposal Brief:</strong> Consolidated management
                      summary for leadership alignment.
                    </li>
                    <li>
                      <strong>Stage 7 Final Submission Package & Master Compiler:</strong> Compiles
                      all sections, matrices, and forms with automated pre-flight validation and Bid
                      Lead sign-off.
                    </li>
                    <li>
                      <strong>Stage 8 Post-Mortem Debrief & FOIA Tracking:</strong> Records outcome
                      factors, tracks public records requests, and logs competitor scorecards.
                    </li>
                    <li>
                      <strong>Header RFP Quick Switcher & Memory:</strong> Instantly jump between
                      active proposal stages without losing page context.
                    </li>
                  </ul>
                </div>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Proposal Writer / Bid Editor"
              badge="Narrative & Section Allocation"
              defaultExpanded={false}
              summary="Crafts compelling narrative with clear section boundaries, target word counts, winning answers, and top-level workspace access."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <strong className="text-slate-900 dark:text-white">Primary Role:</strong> Writes
                  and tailors proposal responses in active, consultative brand voice.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Pain Points Solved:</strong>{" "}
                  Unclear section assignments, scope uncertainty, hunting through fragmented past
                  documents, repetitive copy-pasting, and maintaining voice consistency.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Key Features Used:</strong>
                  <ul className="mt-1.5 list-inside list-disc space-y-1 pl-2">
                    <li>
                      <strong>Unified RFP Workspace & Quick Switcher:</strong> Top-level sidebar
                      navigation with 1-click stage shortcuts to your active drafting sections.
                    </li>
                    <li>
                      <strong>Stage 3 Section Workload View:</strong> Filter requirements by
                      personal assignments with target word budgets and estimated hours.
                    </li>
                    <li>
                      <strong>Stage 4 Section Outline & Hierarchy View:</strong> Group requirements
                      by standard proposal sections (Executive Summary, Technical Approach, Pricing,
                      Experience) with collapsible section cards and assigned SME reviewer tags.
                    </li>
                    <li>
                      <strong>Section Word Budget Progress Meters:</strong> Real-time word count
                      tracking per section comparing actual vs. target words with color-coded
                      threshold alerts (blue, emerald, rose).
                    </li>
                    <li>
                      <strong>Batch Auto-Draft Engine:</strong> 1-click bulk AI drafting across all
                      eligible unanswered requirements using top-ranked historical library
                      precedents with live progress.
                    </li>
                    <li>
                      <strong>Markdown Editor & Live Preview:</strong> Full formatting toolbar
                      (headings, bullet lists, bold, italics, tables, links, quotes) with
                      split/toggle live preview and real-time word counter.
                    </li>
                    <li>
                      <strong>Inline SME Delegation (Ask SME):</strong> Route technical prompts
                      directly to assigned SMEs from the requirement drafting card without leaving
                      the writing flow.
                    </li>
                    <li>
                      <strong>Stage 5 Proposal Writing Coach:</strong> Instant gap detection, style
                      audit, and word budget monitoring.
                    </li>
                    <li>
                      <strong>Stage 7 Master Compiler Preview:</strong> Real-time compiled document
                      preview checking narrative continuity and section transitions.
                    </li>
                    <li>
                      <strong>Stage 8 Past Debrief & Rebid Intelligence:</strong> Reference client
                      quirks and winning answers from past contract cycles.
                    </li>
                  </ul>
                </div>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Subject Matter Expert (SME)"
              badge="Targeted Knowledge & Review"
              defaultExpanded={false}
              summary="Provides technical, legal, security, or pricing details without reading 90-page documents."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <strong className="text-slate-900 dark:text-white">Primary Role:</strong>{" "}
                  Technical lead, operations specialist, or finance lead reviewing technical facts.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Pain Points Solved:</strong>{" "}
                  Repeatedly answering the exact same security/technical questions under tight
                  turnarounds; being asked to review entire 100-page drafts.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Key Features Used:</strong>
                  <ul className="mt-1.5 list-inside list-disc space-y-1 pl-2">
                    <li>
                      <strong>Header Quick Switcher Access:</strong> 1-click deep linking into the
                      active opportunity when asked to review assigned technical sections.
                    </li>
                    <li>
                      <strong>Stage 3 Section SME Reviewer Assignment:</strong> Explicitly assigned
                      only to relevant proposal sections (e.g. Quality, Security, Audio/Video).
                    </li>
                    <li>
                      <strong>Inline Drafting Delegation (Ask SME):</strong> Receives targeted
                      questions routed directly from the Stage 4 Drafting Board by writers.
                    </li>
                    <li>
                      <strong>Review Queue (/reviews):</strong> "Assigned to Me" tab surfaces only
                      their targeted questions with requirement context.
                    </li>
                    <li>
                      <strong>Pre-Ask SME Duplicate Detection:</strong> Automatically prevents
                      asking questions already covered in the Answer Library.
                    </li>
                  </ul>
                </div>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Executive / Decision Committee"
              badge="Strategy & Governance"
              defaultExpanded={false}
              summary="Makes objective Go/No-Go decisions, monitors workload capacity, authorizes plan freeze, and signs off on final submission."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <strong className="text-slate-900 dark:text-white">Primary Role:</strong> Decides
                  whether to bid, sets pricing strategy, authorizes commitments, and signs final
                  submissions.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Pain Points Solved:</strong>{" "}
                  Committing days of proposal effort to low-win or high-risk bids; blind spots on
                  team burnout, unapproved pricing commitments, and lack of historical win/loss
                  data.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Key Features Used:</strong>
                  <ul className="mt-1.5 list-inside list-disc space-y-1 pl-2">
                    <li>
                      <strong>Stage 2 Fit-Scoring Engine:</strong> 0–100% win probability score
                      across capability, domain, capacity, and library depth.
                    </li>
                    <li>
                      <strong>Stage 3 Team Workload Rollup:</strong> Real-time visibility into
                      writer capacity, section allocation, and estimated effort hours.
                    </li>
                    <li>
                      <strong>1-Page Executive Opportunity Brief:</strong> Consolidated snapshot for
                      rapid Go/Caution/No-Go decisions and plan freeze approval.
                    </li>
                    <li>
                      <strong>Stage 7 Executive Sign-Off Gate:</strong> Mandatory leadership
                      authorization of pricing, legal terms, and final package delivery.
                    </li>
                    <li>
                      <strong>Pipeline Win/Loss Analytics:</strong> High-level win rates by service
                      category, client industry, contract size, and loss factor Pareto analysis.
                    </li>
                    <li>
                      <strong>Stage 8 Rebid Engine Approval:</strong> Approves recurring contract
                      recompetes pre-loaded with past institutional memory.
                    </li>
                  </ul>
                </div>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Contracts / Compliance Lead"
              badge="Risk & Governance"
              defaultExpanded={false}
              summary="Audits contract terms, required forms checklist, format constraints, and pre-flight submission validation."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <div>
                  <strong className="text-slate-900 dark:text-white">Primary Role:</strong> Protects
                  MLC legal and financial interests from onerous terms and submission errors.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Pain Points Solved:</strong>{" "}
                  Overlooking onerous terms, indemnification traps, missing mandatory
                  certifications, and unexecuted compliance forms.
                </div>
                <div>
                  <strong className="text-slate-900 dark:text-white">Key Features Used:</strong>
                  <ul className="mt-1.5 list-inside list-disc space-y-1 pl-2">
                    <li>
                      <strong>Stage 2 Red-Flag & Contract Risk Scanner:</strong> Identifies
                      unlimited liability, liquidated damages, and competitor-tailored clauses.
                    </li>
                    <li>
                      <strong>Stage 3 Required Forms Checklist:</strong> Documents mandatory forms,
                      signatory requirements (Officer/Notary), and uploaded file attachments.
                    </li>
                    <li>
                      <strong>Stage 3 Format & Submission Rules:</strong> Enforces margins, font
                      size caps, page limits, delivery portals, and physical binding instructions.
                    </li>
                    <li>
                      <strong>Stage 7 Package Validation Pre-Flight:</strong> Automated audit
                      checking for unverified forms, missing officer signatures, or format
                      discrepancies before dispatch.
                    </li>
                    <li>
                      <strong>Stage 8 FOIA Public Records Tracking:</strong> Tracks public records
                      requests and reviews winning competitor compliance submissions.
                    </li>
                    <li>
                      <strong>RFP Addenda Scope Shift Log:</strong> Audits client amendments for
                      legal and commercial term shifts.
                    </li>
                  </ul>
                </div>
              </div>
            </ExpandableCard>
          </div>
        </div>
      )}

      {/* Tab 3: End-to-End Workflow */}
      {activeTab === "workflow" && (
        <div className="space-y-6">
          {/* Workflow Header Banner */}
          <div className="glass rounded-xl border border-slate-200/80 bg-white/70 p-5 dark:border-slate-800/80 dark:bg-slate-900/70">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              The 12-Step Proposal Lifecycle & 8 Workspace Stages
            </h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              From the moment an RFP document is received through qualification, response planning,
              draft synthesis, adversarial review, master compilation, dual sign-off, debrief, and
              living knowledge enrichment.
            </p>

            {/* Visual Stage Progress Architecture Bar */}
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
              <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-2.5 text-center dark:border-teal-900/50 dark:bg-teal-950/30">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#2ba5b5]">
                  Stage 1
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Intake & Shredder
                </p>
              </div>
              <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-2.5 text-center dark:border-teal-900/50 dark:bg-teal-950/30">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#2ba5b5]">
                  Stage 2
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Strategic Assessment
                </p>
              </div>
              <div className="rounded-lg border border-teal-300 bg-teal-100/70 p-2.5 text-center ring-1 ring-[#2ba5b5]/30 dark:border-teal-700 dark:bg-teal-900/40">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#2ba5b5]">
                  Stage 3 (Core)
                </span>
                <p className="text-xs font-bold text-slate-900 dark:text-white">
                  Response Plan & Freeze
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center dark:border-slate-800 dark:bg-slate-900">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Stage 4
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Dynamic Writing
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center dark:border-slate-800 dark:bg-slate-900">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Stage 5
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Coach Compliance
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center dark:border-slate-800 dark:bg-slate-900">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Stage 6
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Evaluator Red Team
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center dark:border-slate-800 dark:bg-slate-900">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Stage 7
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Final Submission
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center dark:border-slate-800 dark:bg-slate-900">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Stage 8
                </span>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Debrief & Learn
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <ExpandableCard
              title="Step 1: Intake & Shredder Engine"
              badge="Stage 1"
              defaultExpanded={true}
              summary="Upload PDF/DOCX RFPs to automatically extract discrete requirements and submission constraints."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Upload your RFP documents under <strong>Intake & Attachments</strong> in the
                  Opportunity Workspace. Click <strong>"Run Automated Extraction"</strong> to launch
                  the AI shredder.
                </p>
                <p>
                  The engine creates structured requirement line items categorized into Technical,
                  Pricing, Submission Guidelines, and Mandatory Qualifications, while isolating
                  format constraints (page limits, fonts, delivery methods) and required forms.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 2: Strategic Assessment & Go/No-Go"
              badge="Stage 2"
              defaultExpanded={false}
              summary="Analyze win probability, run contract risk scan, and log the Go/No-Go decision."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Open the <strong>Strategic Assessment</strong> tab. The engine evaluates 4
                  pillars: Technical Capability, Domain Experience, Capacity & Turnaround, and
                  Library Depth.
                </p>
                <p>
                  The <strong>Red-Flag Scanner</strong> checks for onerous contract clauses
                  (unlimited liability, aggressive SLAs, missing mandatory qualifications). The
                  1-Page Executive Brief provides a printable summary for leadership to log a formal
                  Go, Caution, or No-Go decision.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 3: Proposal Response Plan, Milestones & Section Ownership"
              badge="Stage 3 (PlanTab)"
              defaultExpanded={true}
              summary="Auto-schedule milestones, track addenda scope shifts, allocate section leads & team workload, verify required forms, and freeze the plan."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Open the <strong>Response Plan</strong> tab. Stage 3 provides 7 specialized
                  sub-tabs that ensure total proposal readiness:
                </p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Response Schedule & Milestones:</strong> Auto-generates color-team
                    milestones (Kickoff, First Draft, Pink Team, Red Team, Gold Team, Submission)
                    calculated from the due date and business-day offsets in Settings.
                  </li>
                  <li>
                    <strong>RFP Addenda & Scope Shift Log:</strong> Documents client amendments,
                    scope impact ratings, deadline shifts, and links affected requirements.
                  </li>
                  <li>
                    <strong>Requirements Matrix & Category Rollups:</strong> Filters requirements,
                    displays completion progress meters, and runs multi-select bulk operations (bulk
                    assign owner, bulk confirm, bulk category change, bulk delete).
                  </li>
                  <li>
                    <strong>Bid Section Ownership & Workload Allocation:</strong> Organizes
                    requirements into proposal sections (Executive Summary, Technical, Pricing,
                    Experience, Quality, Staffing). Designates Section Leads, Writers, and SME
                    Reviewers with target word counts and estimated effort hours.
                  </li>
                  <li>
                    <strong>Team Workload Rollup:</strong> Monitors writer and SME capacity to
                    prevent burnout, with interactive filtering by individual team member.
                  </li>
                  <li>
                    <strong>Required Forms Checklist:</strong> Documents mandatory forms, signature
                    authorities (Officer/Notary), and uploaded document linkages.
                  </li>
                  <li>
                    <strong>Format & Submission Rules:</strong> Enforces document styling (fonts,
                    margins, caps) and submission logistics (portals, packaging).
                  </li>
                  <li>
                    <strong>1-Page Executive Proposal Brief:</strong> Clean consolidated management
                    summary for leadership alignment.
                  </li>
                </ul>
                <div className="rounded-lg bg-teal-50/70 p-3 dark:bg-teal-950/40">
                  <span className="font-semibold text-slate-900 dark:text-white">
                    Plan Readiness Audit & Freeze Gate:
                  </span>
                  <p className="mt-1 text-slate-600 dark:text-slate-400">
                    Click <strong>"Plan Readiness Audit"</strong> to check the 5 core planning
                    criteria. Once verified with a 100% score, click{" "}
                    <strong>"Freeze Response Plan"</strong> to lock the baseline and activate Stage
                    4 Drafting. Export the full plan at any time via{" "}
                    <strong>"Export Planning Package"</strong>.
                  </p>
                </div>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 4: Stage 4 Drafting Engine Overhaul & Content Assembly"
              badge="Stage 4 (DraftingTab)"
              defaultExpanded={false}
              summary="Section outline hierarchy, real-time word budget meters, 1-click batch auto-drafting, rich markdown editor with live preview, and inline SME routing."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Open the <strong>Drafting Board</strong> tab (<code>drafting</code>). Stage 4
                  provides a modern, streamlined proposal authoring studio powered by historical win
                  intelligence and structured section governance:
                </p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Section Outline vs. Flat View:</strong> Toggle between the structured
                    proposal section hierarchy (Executive Summary, Technical Approach, Pricing,
                    Experience, Quality, Staffing—mapped directly from Stage 3 Section Allocation)
                    and a flat list of requirements. Each section card highlights its Section Lead,
                    assigned SME Reviewer, total requirements, and word count progress.
                  </li>
                  <li>
                    <strong>Section Word Budget Progress Meters:</strong> Real-time word counters
                    track actual words against allocated target budgets with dynamic percentage
                    calculations and color-coded threshold meters:
                    <span className="ml-1 text-[#2ba5b5] font-semibold">
                      &lt;70% (Under Budget)
                    </span>
                    ,
                    <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                      70%–110% (Optimal)
                    </span>
                    , and
                    <span className="ml-1 text-rose-500 font-semibold">
                      &gt;110% (Over Budget Warning)
                    </span>
                    .
                  </li>
                  <li>
                    <strong>Batch Auto-Draft Engine:</strong> Click{" "}
                    <strong>"Batch Auto-Draft"</strong> to launch the automated drafting modal. It
                    audits all proposal requirements, filters for eligible unanswered questions with
                    verified library matches (&ge; 30% confidence), and drafts complete grounded
                    narrative sections sequentially with live progress tracking and cancellation
                    controls.
                  </li>
                  <li>
                    <strong>Rich Markdown Editor & Live Preview:</strong> Toggle between the
                    markdown source editor and formatted preview. Includes a formatting toolbar
                    (H1–H3, Bold, Italic, Bullet Lists, Numbered Lists, Blockquotes, Tables, Code,
                    and Links) with a live word counter and word budget compliance indicator.
                  </li>
                  <li>
                    <strong>Inline SME Task Routing (Ask SME):</strong> Delegate difficult technical
                    or pricing prompts directly to assigned SMEs via the inline "Ask SME" button on
                    any requirement card. Pre-ask duplicate detection checks the Answer Library
                    first to prevent redundant requests.
                  </li>
                  <li>
                    <strong>Win-Weighted Library Sourcing:</strong> Semantic search surfaces
                    verified library answers with past-win weighting (+0.05 per win, cap +0.10) so
                    proven winning arguments appear first.
                  </li>
                </ul>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 5: Proposal Writing Coach Audit"
              badge="Stage 5"
              defaultExpanded={false}
              summary="Run line-by-line compliance and completeness audits with 1-click suggested fixes."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Navigate to <strong>Completeness & Coach</strong> tab and click{" "}
                  <strong>"Run Proposal Coach Audit"</strong>. The Coach audits compliance,
                  word/page budgets, and missing forms.
                </p>
                <p>
                  Suggestions are categorized by severity. Click <strong>"Apply Suggestion"</strong>{" "}
                  to stage proposed fixes directly into the drafting editor.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 6: Evaluator Red Team (The AI Client Red-Teamer)"
              badge="Stage 6"
              defaultExpanded={false}
              summary="Simulate a skeptical client procurement committee with letter grade and weakness scan."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  On the <strong>Evaluator Red Team</strong> tab, click{" "}
                  <strong>"Run Red Team Evaluation"</strong>. The evaluator scores the proposal
                  across 4 procurement dimensions: Technical Excellence (35%), Compliance & Risk
                  (25%), Price/Value (20%), and Past Performance (20%).
                </p>
                <p>
                  It highlights fluffy or unsupported claims, unaddressed client pain points, and
                  runs the <strong>Pure Freshness Scanner</strong> to catch outdated statistics or
                  retired citations.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 7: Final Submission Package Compiler, Checklist & Dual Sign-Off Gate"
              badge="Stage 7 (SubmitTab)"
              defaultExpanded={false}
              summary="Track submission deliverables, compile master proposals, run pre-flight package validation, and enforce dual Bid Lead and Executive sign-offs."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Navigate to the <strong>Final Submission</strong> tab in the Opportunity
                  Workspace. Stage 7 serves as the mission-critical assembly, validation, and
                  executive governance gate before delivering proposals to the client.
                </p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Submission Package Checklist:</strong> Tracks all deliverable items
                    (Master Proposal Narrative, Completed Pricing Schedule, Mandatory Compliance
                    Forms, Certificates/Affidavits, Client Portal Access). Toggle statuses between{" "}
                    <em>Pending</em>, <em>In Progress</em>, and <em>Ready</em>.
                  </li>
                  <li>
                    <strong>Automated Master Proposal Compiler:</strong> Merges approved draft
                    sections, requirements compliance matrix, executive summary, company profile,
                    and appendices into a unified client-ready document with live word counts and
                    page estimates.
                  </li>
                  <li>
                    <strong>Automated Package Validation Pre-Flight:</strong> Runs pre-flight checks
                    identifying blocking issues: unanswered mandatory requirements, unassigned
                    required forms, missing section leads, unapproved drafts, or format rule
                    violations.
                  </li>
                  <li>
                    <strong>Mandatory Dual Sign-Off Gate:</strong> Enforces two distinct sign-off
                    authorizations:
                    <ul className="mt-1 list-inside list-circle space-y-0.5 pl-4 text-[11px]">
                      <li>
                        <strong>Bid / Proposal Lead Sign-Off:</strong> Verifies narrative
                        completeness, compliance verification, and submission package layout.
                      </li>
                      <li>
                        <strong>Executive Sign-Off:</strong> Authorizes pricing, legal commitments,
                        indemnifications, and corporate delivery.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Record Submission Modal:</strong> Click{" "}
                    <strong>"Record Submission"</strong> to log confirmation/tracking numbers, exact
                    timestamps, delivery portals (Bonfire, IonWave, Jaggaer, Email, Courier),
                    recipient contacts, and confirmation notes.
                  </li>
                  <li>
                    <strong>Export Formats:</strong> Export the compiled master proposal to Google
                    Docs (rich clipboard copy), Microsoft Word, Markdown (.md), CSV checklist, or
                    JSON audit manifest.
                  </li>
                </ul>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 8: Post-Mortem Debrief, FOIA Scorecards, Win/Loss Analytics & Rebid Engine"
              badge="Stage 8 (DebriefTab)"
              defaultExpanded={false}
              summary="Capture structured bid outcomes, track FOIA public records requests, log competitor scorecards, analyze win/loss trends, and clone 1-click rebids."
            >
              <div className="space-y-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Open the <strong>Debrief & Learn</strong> tab in the Opportunity Workspace or
                  click <strong>"Record Outcome & Debrief"</strong> on any submitted RFP.
                </p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Post-Mortem Outcome Debrief:</strong> Record the formal outcome (
                    <strong className="text-emerald-600 dark:text-emerald-400">Won 🏆</strong>,{" "}
                    <strong className="text-rose-600 dark:text-rose-400">Lost</strong>,{" "}
                    <em>Disqualified</em>, or <em>Withdrawn</em>) along with multi-factor tags
                    (Pricing, Technical, Domain Experience, Turnaround, Compliance, Incumbent
                    Advantage, Agency Relationship), client feedback quotes, and internal lessons
                    learned.
                  </li>
                  <li>
                    <strong>FOIA & Public Records Request Tracking:</strong> Track state and
                    municipal public records requests with statuses (<em>Requested</em>,{" "}
                    <em>Acknowledged</em>, <em>Records Received</em>, <em>Denied</em>), tracking
                    numbers, request dates, and uploaded responsive competitor proposals.
                  </li>
                  <li>
                    <strong>Competitor Scorecard & Rubric Analysis:</strong> Document winning
                    competitor names, winning price variance vs MLC, and points breakdowns across
                    evaluation rubric categories.
                  </li>
                  <li>
                    <strong>1-Click Rebid Engine:</strong> For expiring multi-year contracts or
                    recompetes, click <strong>"Initialize Rebid"</strong>. The engine clones the RFP
                    into a new opportunity, links to the historical bid record, and pre-loads
                    lessons learned, client quirks, and winning answers.
                  </li>
                  <li>
                    <strong>Pipeline Win/Loss Analytics:</strong> Click{" "}
                    <strong>"Win/Loss Analytics"</strong> on the Pipeline view to visualize overall
                    win rates, service category breakdowns, industry trends, and root-cause loss
                    factor distributions.
                  </li>
                </ul>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 9: SME Task Routing & Resolution"
              badge="SME Workflow"
              defaultExpanded={false}
              summary="Route low-confidence questions to subject matter experts with Pre-Ask duplicate detection."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  For requirements where library confidence is low, click <strong>"Ask SME"</strong>
                  . The system first checks the library for matches (&ge; 50% similarity) to prevent
                  duplicate questions.
                </p>
                <p>
                  Assigned SMEs find their tasks in the <strong>Review Queue (/reviews)</strong>,
                  submit answers, and the proposal writer can review, accept, or return the response
                  with feedback.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 10: Final Assembly & Formatted Google Docs Export"
              badge="Export"
              defaultExpanded={false}
              summary="Export cleanly formatted proposals directly to Google Docs or Word with preserved styling."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  When all sections are approved, click{" "}
                  <strong>"Copy Formatted (Google Docs)"</strong> to copy rich text to your
                  clipboard. Paste into Google Docs or Word with preserved headings, tables, and
                  lists.
                </p>
                <p>
                  Alternatively, click <strong>"Download Markdown"</strong> for a complete `.md`
                  bundle.
                </p>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 11: Living Library Governance, Permanent Deletion vs Safe Retirement"
              badge="Living Library & Governance"
              defaultExpanded={false}
              summary="Harvest new answers with client redaction, assign win credit, manage answer retirement vs permanent deletion, and run freshness audits."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>Proposal HQ operates a self-enriching knowledge loop with strict governance:</p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Harvest to Library:</strong> Click <strong>"Harvest to Library"</strong>{" "}
                    on the Drafting Board or Debrief Tab. AI sanitizes prospective client names and
                    financials via <code>sanitizeClient</code>, staging candidate answers with
                    status <em>review_due</em>.
                  </li>
                  <li>
                    <strong>Win Credit Attribution:</strong> Answers used in winning drafts receive
                    permanent <strong>🏆 Won in N proposals</strong> badges and priority confidence
                    boosts in future bids.
                  </li>
                  <li>
                    <strong>Safe Retirement vs Permanent Deletion:</strong> Outdated answers can be{" "}
                    <em>Safely Retired</em> (hiding them from new matching while preserving past
                    proposal citations and win credit) or <em>Permanently Deleted</em> with
                    confirmation safeguard modals that audit active proposal dependencies.
                  </li>
                  <li>
                    <strong>2026 Master RFP Template Quick-Seed:</strong> In the Answer Library,
                    click <strong>"Import Proposals"</strong> and select{" "}
                    <strong>"Seed Master Template"</strong> to instantly hydrate 48 pre-verified,
                    gold-standard boilerplate answers covering Company Overview, WBE Certification,
                    Security & Compliance, Quality Management, Translation, Interpreting, and
                    Disaster Recovery.
                  </li>
                  <li>
                    <strong>Drag-and-Drop Past Proposal Importer:</strong> Drag and drop past RFP
                    response documents (PDF, DOCX) directly into the upload drop zone for automated
                    parsing, chunking, and candidate answer extraction.
                  </li>
                  <li>
                    <strong>Pure Freshness Scanner:</strong> In{" "}
                    <strong>Review Queue (/reviews)</strong>, check <em>Freshness Findings</em> to
                    renew expired client references, update stale statistics, and reassign departed
                    staff owners.
                  </li>
                </ul>
              </div>
            </ExpandableCard>

            <ExpandableCard
              title="Step 12: Primary RFP Workspace Navigation, Workspace Picker & Header Quick Switcher"
              badge="Navigation & Memory"
              defaultExpanded={false}
              summary="Effortlessly navigate active proposals with top-level sidebar navigation, searchable Workspace Picker, and persistent Header Quick Switcher with 1-click stage tab jumps."
            >
              <div className="space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <p>
                  Proposal HQ provides unified proposal navigation across the entire web
                  application:
                </p>
                <ul className="list-inside list-disc space-y-1.5 pl-2">
                  <li>
                    <strong>Primary Sidebar Destination:</strong> Click <strong>Workspace</strong> (
                    <code>/workspace</code>) in the left sidebar to immediately resume your active
                    RFP without losing your position.
                  </li>
                  <li>
                    <strong>Searchable Workspace Picker:</strong> If no active RFP is selected, or
                    when clicking <strong>"Switch RFP"</strong>, the Workspace Picker provides live
                    search, stage tabs (All, Active, Submitted, Won/Lost), summary pipeline KPIs,
                    and rich proposal cards.
                  </li>
                  <li>
                    <strong>Persistent Header Quick Switcher:</strong> Located in the top header on
                    every page. Displays the active proposal title, client, stage badge, and days
                    remaining countdown.
                  </li>
                  <li>
                    <strong>1-Click Stage Tab Shortcuts:</strong> Open the switcher dropdown to
                    instantly jump straight to <strong>Intake</strong>, <strong>Assessment</strong>,{" "}
                    <strong>Plan</strong>, <strong>Drafting</strong>, <strong>Coach</strong>,{" "}
                    <strong>Red Team</strong>, <strong>Submit</strong>, or <strong>Debrief</strong>.
                  </li>
                  <li>
                    <strong>Recent RFPs & Memory:</strong> Quick-switch to recent proposals and
                    enjoy cross-tab memory synchronization.
                  </li>
                </ul>
              </div>
            </ExpandableCard>
          </div>
        </div>
      )}

      {/* Tab 4: Step-by-Step Instructions & FAQ */}
      {activeTab === "instructions" && (
        <div className="space-y-6">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides, workflows, and FAQs (e.g., 'batch auto-draft', 'word budget', 'markdown editor', 'master template', 'final submission', 'sign-off', 'debrief', 'foia', 'rebid', 'workspace switcher')..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 shadow-2xs focus:border-[#2ba5b5] focus:outline-hidden focus:ring-1 focus:ring-[#2ba5b5] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <div className="space-y-4">
            {filteredTopics.map((topic) => (
              <ExpandableCard
                key={topic.id}
                title={topic.title}
                badge={topic.category.toUpperCase()}
                badgeColor={
                  topic.category === "module"
                    ? "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
                    : topic.category === "rules"
                      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                      : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300"
                }
                summary={topic.summary}
                headerActions={
                  topic.suggestedLinks && topic.suggestedLinks.length > 0 ? (
                    <div className="flex items-center gap-1.5">
                      {topic.suggestedLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className="btn-animate inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-1 text-[11px] font-medium text-[#2ba5b5] hover:bg-teal-100 dark:bg-teal-950/50 dark:text-teal-300"
                        >
                          <span>{link.label}</span>
                          <ArrowRight className="h-2.5 w-2.5" />
                        </Link>
                      ))}
                    </div>
                  ) : undefined
                }
              >
                <div className="whitespace-pre-line text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                  {topic.content}
                </div>
              </ExpandableCard>
            ))}

            {filteredTopics.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                No guides or topics match &ldquo;{searchQuery}&rdquo;. Try asking the{" "}
                <button
                  type="button"
                  onClick={() => setActiveTab("assistant")}
                  className="font-semibold text-[#2ba5b5] underline"
                >
                  Interactive Assistant
                </button>
                !
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
